<template>
  <UDashboardPanel id="workflows-public" :ui="{ body: 'h-full w-full sm:p-0 p-0' }">
    <template #header>
      <UDashboardNavbar :toggle="false" :class="[workflow && isVerifiedState ? ([WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.status) ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800/40' : 'bg-primary-100 dark:bg-primary-900/30 border-transparent') : 'border-transparent', 'transition-colors duration-200']">
        <template #leading />
        <template #title>
          <svg v-if="!isLoggedIn && !workflow" width="30" height="68" viewBox="0 0 80 68" fill="none" xmlns="http://www.w3.org/2000/svg" style="width: 80%; margin: 0 auto;">
            <g clip-path="url(#clip0_38_84)">
              <path d="M79.9607 68L48.5082 0H31.4304L50.2282 52.1457C50.904 53.694 51.5797 55.428 52.2554 57.0382C53.2383 59.4535 54.1598 61.745 54.8355 63.3552C55.5113 64.9654 55.2041 64.2841 55.327 64.6557C56.8013 68.0619 60.5486 68.0619 60.5486 68H79.9607L71.9747 51.2787Z" fill="url(#paint0_linear_38_84)"/>
              <path d="M0.0393066 68L31.4919 0H48.5696L29.7718 52.1457C29.0961 53.694 28.4203 55.428 27.7446 57.0382C26.7617 59.4535 25.8402 61.745 25.1645 63.3552C24.9802 63.8506 24.7959 64.2841 24.673 64.6557C23.1987 68.0619 19.4514 68.0619 19.4514 68H0.0393066L8.0253 51.2787Z" fill="#C8017D"/>
            </g>
            <defs>
              <linearGradient id="paint0_linear_38_84" x1="30.2632" y1="1.17668" x2="78.3918" y2="69.4295" gradientUnits="userSpaceOnUse">
                <stop offset="0.2" stop-color="#186BA1"/>
                <stop offset="0.3" stop-color="#078FCB"/>
                <stop offset="0.4" stop-color="#00A0DF"/>
                <stop offset="1" stop-color="#00A0DF"/>
              </linearGradient>
              <clipPath id="clip0_38_84">
                <rect width="80" height="68" fill="white"/>
              </clipPath>
            </defs>
          </svg>
          
          <span v-if="!isLoggedIn && !workflow" class="text-lg font-semibold text-gray-800 dark:text-gray-200">
            Drivalia eOrders
          </span>
          
          <div v-if="workflow && isVerifiedState" class="flex items-center gap-1.5 min-w-0 flex-1 w-full sm:w-auto ml-0 sm:ml-4">
            <UIcon v-if="[WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.status)" name="i-lucide-circle-dollar-sign" class="w-6 h-6 text-blue-600 dark:text-blue-400 shrink-0 hidden sm:block" />
            
            <div v-if="[WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.status) && workflow.quotation?.description" class="bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-md flex items-center gap-1.5 min-w-0 border border-blue-200 dark:border-blue-700/40 sm:max-w-none">
              <span class="text-xs sm:text-sm font-semibold text-blue-700 dark:text-blue-300 truncate">{{ workflow.quotation?.description }}</span>
            </div>
            
            <UTooltip v-if="workflow.vehicle" :text="`${workflow.vehicle.manufacturer} ${[getSapLocalizedText(workflow.vehicle.modelDescription, locale) !== '-' ? getSapLocalizedText(workflow.vehicle.modelDescription, locale) : [workflow.vehicle.model, workflow.vehicle.trim].filter(Boolean).join(' '), workflow.vehicle.category, workflow.vehicle.powerKW ? workflow.vehicle.powerKW + 'kW' : '', workflow.vehicle.transmission, workflow.vehicle.homologation].filter(Boolean).join(', ')}`" :delay-duration="300">
              <span class="text-xs sm:text-base font-semibold text-gray-900 dark:text-gray-100 truncate sm:max-w-2xl">{{ workflow.vehicle.manufacturer }} {{ [getSapLocalizedText(workflow.vehicle.modelDescription, locale) !== '-' ? getSapLocalizedText(workflow.vehicle.modelDescription, locale) : [workflow.vehicle.model, workflow.vehicle.trim].filter(Boolean).join(' '), workflow.vehicle.category, workflow.vehicle.powerKW ? workflow.vehicle.powerKW + 'kW' : '', workflow.vehicle.transmission, workflow.vehicle.homologation].filter(Boolean).join(', ') }}</span>
            </UTooltip>
            <UTooltip v-else :text="workflow.workflowName" :delay-duration="300">
              <span class="text-xs sm:text-base font-semibold text-gray-900 dark:text-gray-100 truncate sm:max-w-2xl">{{ workflow.workflowName }}</span>
            </UTooltip>
          </div>
        </template>
        <template #right>
          <div class="flex items-center gap-1.5 ml-auto shrink-0 max-w-full overflow-x-auto">
            <div v-if="workflow && isVerifiedState" class="flex items-center gap-1.5 shrink-0">
              <div class="flex items-center gap-0.5 rounded-lg border px-1 py-1 shadow-none sm:shadow-sm backdrop-blur-sm transition-all duration-150" :class="[WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.status) ? 'bg-white/75 dark:bg-blue-950/20 border-blue-200/80 dark:border-blue-700/60' : 'bg-white/75 dark:bg-primary-950/20 border-primary-200/80 dark:border-primary-700/60'">
                <UTooltip :delay-duration="0" :text="pdfTooltip">
                  <UButton
                    color="secondary"
                    variant="soft"
                    icon="i-heroicons-document-arrow-down"
                    size="sm"
                    :class="['h-7 rounded-lg whitespace-nowrap transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:focus-visible:ring-primary-400/60 focus-visible:ring-offset-1 focus-visible:ring-offset-white/70 dark:focus-visible:ring-offset-gray-900/70', [WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.status) ? '!text-blue-900 !bg-blue-200/65 hover:!bg-blue-200/85 active:!bg-blue-300/70 dark:!text-blue-100 dark:!bg-blue-700/45 dark:hover:!bg-blue-700/55 dark:active:!bg-blue-700/60' : '!text-primary-800 !bg-primary-100 hover:!bg-primary-200/70 active:!bg-primary-200 dark:!text-primary-200 dark:!bg-primary-800/40 dark:hover:!bg-primary-800/55 dark:active:!bg-primary-800/60']"
                    tabindex="-1"
                    :disabled="pdfDownloading"
                    :loading="pdfDownloading"
                    @click="downloadPdf"
                  >
                    {{ t('workflowDetail.header.pdf') }}
                  </UButton>
                </UTooltip>
              </div>
              <UTooltip :delay-duration="0" :text="t('workflowDetail.header.quotationNumber')" v-if="workflow?.quotation?.number">
                <UBadge color="secondary" variant="soft" size="lg" :class="['font-mono font-semibold rounded-full', [WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.status) ? 'bg-blue-50/75 text-blue-700/90 ring-1 ring-blue-300/50 dark:bg-blue-900/15 dark:text-blue-300/90 dark:ring-blue-700/50' : 'text-secondary/90 ring-1 ring-gray-300/50 dark:bg-gray-200/10 dark:text-gray-200/90 dark:ring-gray-600/50']">
                  <UIcon name="i-heroicons-calculator" class="w-4 h-4 mr-1 hidden sm:inline-block"/>
                  {{ workflow?.quotation?.number }}
                </UBadge>
              </UTooltip>
            </div>
            
            <div class="sm:hidden shrink-0">
              <LanguageSwitcher :collapsed="true" />
            </div>
            <div class="hidden sm:block shrink-0">
              <LanguageSwitcher />
            </div>
          </div>
        </template>
      </UDashboardNavbar>
    </template>
    
    <template #body>
      <div v-if="flowState === 'initial'" class="flex items-center justify-center h-full p-4">
        <UCard class="w-full max-w-md shadow-xl">
          <template #header>
            <div class="text-center">
              <UIcon name="i-heroicons-lock-closed" class="w-12 h-12 text-primary-500 mx-auto mb-2" />
              <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('workflowPublic.protectedAccess') }}</h3>
              <p class="text-sm text-gray-500 mt-1">{{ t('workflowPublic.description') }}</p>
            </div>
          </template>

          <div class="space-y-4">
            <p class="text-center text-gray-600 dark:text-gray-300">
              {{ t('workflowPublic.requestCodeDescription') }}
            </p>
            <UButton block size="lg" @click="requestCode" :loading="loading">
              {{ t('workflowPublic.sendAccessCode') }}
            </UButton>
          </div>
        </UCard>
      </div>
      
      <div v-else-if="flowState === 'code_sent' || flowState === 'verifying'" class="flex items-center justify-center h-full p-4">
        <UCard class="w-full max-w-md shadow-xl">
          <template #header>
            <div class="text-center">
              <UIcon name="i-heroicons-envelope" class="w-12 h-12 text-primary-500 mx-auto mb-2" />
              <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('workflowPublic.enterCode') }}</h3>
              <p class="text-sm text-gray-500 mt-1">{{ t('workflowPublic.codeSentDescription') }}</p>
            </div>
          </template>
          
          <div class="space-y-6">
            <div class="flex justify-center">
              <UPinInput
                v-model="otpCode"
                :length="6"
                autofocus
                @complete="verifyCode"
                :disabled="flowState === 'verifying'"
              />
            </div>
            
            <UButton
              block
              size="lg"
              @click="verifyCode"
              :disabled="otpCode.length < 6"
              :loading="flowState === 'verifying'"
            >
              {{ t('workflowPublic.verifyAccess') }}
            </UButton>
            
            <div class="text-center space-y-2">
              <UButton
                variant="link"
                color="neutral"
                size="sm"
                @click="resendCode"
                :disabled="resendDisabled"
              >
                {{ resendButtonText }}
              </UButton>
              <p v-if="resendCount >= MAX_RESENDS" class="text-xs text-warning-500">
                {{ t('workflowPublic.resendLimitReached') }}
              </p>
            </div>
          </div>
        </UCard>
      </div>
      
      <div v-else-if="flowState === 'verified' || flowState === 'approving' || flowState === 'approved'" class="p-4 space-y-6">
        <!--
        <UCard v-if="!isLoggedIn" class="bg-gradient-to-r from-primary-50 to-blue-50 border-primary-100">
          <div class="flex items-center justify-between p-0">
            <div class="flex-1">
              <div class="flex items-center space-x-3 mb-2">
                <UIcon name="i-heroicons-star" class="h-7 w-7 text-primary-500"/>
                <h3 class="text-xl font-semibold text-primary-900">Unlock all eOrders features!</h3>
              </div>
              <p class="text-secondary mb-2 max-w-full text-base">
                Want access to eOrders? Sign in to use advanced features like real-time order tracking and order
                history. If you don't have access yet, please contact your Drivalia representative.
              </p>
              <div class="flex items-center space-x-4 text-sm text-secondary mt-4">
                <div class="flex items-center space-x-1">
                  <UIcon name="i-heroicons-check-circle" class="h-4 w-4 text-green-600"/>
                  <span>Order tracking</span>
                </div>
                <div class="flex items-center space-x-1">
                  <UIcon name="i-heroicons-check-circle" class="h-4 w-4 text-green-600"/>
                  <span>Purchase history</span>
                </div>
              </div>
            </div>
          </div>
        </UCard>
        -->
        
        <div class="mb-3" v-if="workflow">
          <WorkflowProcessStatus :workflow="workflow" :driver="driver" :driverLoading="driverLoading" :slug="slug" :slug-token="slugToken" :purchaseOrderIndex="0" :purchaseOrderNumber="null" @reload="reloadWorkflow" />
        </div>
        
        <UTabs v-if="workflow" v-model="activeTab" :items="tabItems" class="w-full mt-1" color="neutral" :ui="{ list: 'sticky top-0 z-10 bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur rounded-xl border border-gray-300/60 dark:border-gray-700/70 shadow-sm px-1 overflow-x-auto', indicator: 'bg-transparent', trigger: 'rounded-lg px-1.5 py-1.5 sm:px-3 text-gray-700 dark:text-gray-200 hover:bg-gray-200/70 dark:hover:bg-gray-700/50 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm data-[state=active]:ring-1 data-[state=active]:ring-gray-300 dark:data-[state=active]:bg-gray-900 dark:data-[state=active]:text-gray-100 dark:data-[state=active]:ring-gray-600 cursor-pointer transition [&_[data-slot=label]]:hidden [&_[data-slot=label]]:sm:inline' }">
          <template #content="{ item }">
            <div class="h-full overflow-y-auto">
              <div v-if="item.value === 'overview'">
                <WorkflowOverview :workflow="workflow" :driver="driver" :driverLoading="driverLoading" :driverError="driverError" :contactPerson="contactPerson" :contactPersonLoading="contactPersonLoading" :contactPersonError="contactPersonError" :costCenter="costCenter" :costCenterLoading="costCenterLoading" :costCenterError="costCenterError" :supplierSalesPerson="supplierSalesPerson" :supplierSalesPersonLoading="supplierSalesPersonLoading" :supplierSalesPersonError="supplierSalesPersonError" :supplier="supplier" :supplierLoading="supplierLoading" :supplierError="supplierError" :isEditMode="false" :purchaseOrderIndex="0" :isPublicView="true" :viewQuotationData="true" />
              </div>
              
              <div v-if="item.value === 'additional'">
                <WorkflowAdditionalInformation :workflow="workflow" :isPublicView="true" />
              </div>
            </div>
          </template>
        </UTabs>
      </div>
      
      <div v-else-if="flowState === 'error'" class="flex flex-col items-center justify-center h-full p-4">
        <UAlert
          :title="errorConfig.title"
          :description="errorMessage || ''"
          :color="errorConfig.color"
          variant="soft"
          :icon="errorConfig.icon"
          class="max-w-md"
        />
        
        <div v-if="errorType === 'rate_limited' && retryAfterSeconds && retryAfterSeconds > 0" class="mt-4 text-center">
          <p class="text-sm text-gray-500">
            {{ t('workflowPublic.retryIn', { minutes: Math.ceil(retryAfterSeconds / 60) }) }}
          </p>
        </div>
        
        <div v-if="isRecoverableError" class="mt-4">
          <UButton
            size="sm"
            @click="handleRetry"
            :disabled="errorType === 'rate_limited' && retryAfterSeconds !== null && retryAfterSeconds > 0"
          >
            {{ t('workflowPublic.tryAgain') }}
          </UButton>
        </div>
      </div>
    </template>
  </UDashboardPanel>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import { usePageTitle } from '@/composables/usePageTitle'
import { useRoute, useRouter } from 'vue-router'
import { useColorMode } from '#imports'
import { useUser } from "@/store"
import { api, DriverDto, PersonPartner, ContactPersonDto, SupplierSalesPersonDto, type CostCenterDto, type OrganizationPartnerDto, type SupplierPartnerDto, type WorkflowDto, type ItemDto, WorkflowStatus, isApiException } from '@/api/eOrdersApi'
import { useApiAdapter } from "@/store"
import { useI18n } from "vue-i18n"
import { showError, showSuccess } from "@/utils/utils.ts"
import { deserializeJson } from "@/utils/jsonDeserializer.ts"
import { getSapLocalizedText } from "@/utils/sapLocalization"
import { useWorkflowEdit } from "@/store/workflowEdit"
import md5 from 'crypto-js/md5'

type SupplierLike = SupplierPartnerDto | OrganizationPartnerDto;

type SlugState = 'initial' | 'code_sent' | 'verifying' | 'verified' | 'approving' | 'approved' | 'error'
type ErrorType = 'expired' | 'invalid_code' | 'rate_limited' | 'unauthorized' | 'server_error' | null

const MAX_RESENDS = 5
const RESEND_COOLDOWN_SECONDS = 30

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const colorMode = useColorMode()
const userStore = useUser()
const apiAdapter = useApiAdapter()
const workflowEditStore = useWorkflowEdit()

colorMode.preference = 'light'

const slug = computed(() => route.params.slug as string)
const flowState = ref<SlugState>('initial')
const workflow = ref<WorkflowDto | null>(null)
const loading = ref(false)
const otpCode = ref<string[]>([])
const errorType = ref<ErrorType>(null)
const errorMessage = ref<string | null>(null)
const retryAfterSeconds = ref<number | null>(null)
const slugToken = ref<string | undefined>(undefined)
const resendCount = ref(0)
const resendCooldown = ref(false)
const cooldownTimer = ref<number | null>(null)
const driver = ref<DriverDto | null>(null)
const driverLoading = ref(false)
const driverError = ref(false)
const contactPerson = ref<ContactPersonDto | null>(null)
const contactPersonLoading = ref(false)
const contactPersonError = ref(false)
const costCenter = ref<CostCenterDto | null>(null)
const costCenterLoading = ref(false)
const costCenterError = ref(false)
const supplierSalesPerson = ref<SupplierSalesPersonDto | null>(null)
const supplierSalesPersonLoading = ref(false)
const supplierSalesPersonError = ref(false)
const supplier = ref<SupplierLike | null>(null)
const supplierLoading = ref(false)
const supplierError = ref(false)
const contentManagerItems = ref<ItemDto[]>([])
const pdfDownloading = ref(false)

const activeContentManagerItem = computed(() => {
  if (!workflow.value || contentManagerItems.value.length === 0) return null;
  const status = workflow.value.status;
  const targetItemType = [WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(status) ? 'quotation' : 'purchase_order';
  
  return contentManagerItems.value.find(item => item.itemtypeName === targetItemType) ?? contentManagerItems.value[0] ?? null;
});

const pdfTooltip = computed(() => {
  if (!workflow.value || [WorkflowStatus.QuotationApproval, WorkflowStatus.QuotationRejected].includes(workflow.value.status)) {
    return t('workflowDetail.header.downloadCalculation');
  }
  
  return t('workflowDetail.header.downloadPurchaseOrder');
})

let retryCountdownInterval: ReturnType<typeof setInterval> | null = null
let resendCooldownInterval: ReturnType<typeof setInterval> | null = null

usePageTitle(computed(() => workflow.value?.externalReference))

const isLoggedIn = computed(() => userStore.isAuthenticated())
const isVerifiedState = computed(() => ['verified', 'approving', 'approved'].includes(flowState.value))

const resendDisabled = computed(() =>
  resendCount.value >= MAX_RESENDS || resendCooldown.value || flowState.value === 'verifying'
)

const resendButtonText = computed(() => {
  if (resendCooldown.value && cooldownTimer.value) {
    return t('workflowPublic.resendIn', { seconds: cooldownTimer.value })
  }
  
  return t('workflowPublic.resendCode')
})

const isRecoverableError = computed(() => 
  errorType.value === 'rate_limited' || errorType.value === 'server_error'
)

const errorConfig = computed(() => {
  const configs: Record<NonNullable<ErrorType>, { title: string; color: 'error' | 'warning'; icon: string }> = {
    expired: {
      title: t('workflowPublic.errorTitles.expired'),
      color: 'error',
      icon: 'i-heroicons-clock'
    },
    invalid_code: {
      title: t('workflowPublic.errorTitles.invalidCode'),
      color: 'error',
      icon: 'i-heroicons-exclamation-triangle'
    },
    rate_limited: {
      title: t('workflowPublic.errorTitles.rateLimited'),
      color: 'warning',
      icon: 'i-heroicons-clock'
    },
    unauthorized: {
      title: t('workflowPublic.errorTitles.unauthorized'),
      color: 'error',
      icon: 'i-heroicons-exclamation-triangle'
    },
    server_error: {
      title: t('workflowPublic.errorTitles.serverError'),
      color: 'error',
      icon: 'i-heroicons-exclamation-triangle'
    }
  }
  
  return errorType.value ? configs[errorType.value] : { title: 'Error', color: 'error' as const, icon: 'i-heroicons-exclamation-triangle' }
})

const activeTab = computed({
  get() {
    return (route.query.tab as string) || 'overview'
  },
  set(tab) {
    router.push({
      query: {
        ...route.query,
        tab
      }
    })
  }
})

const tabItems = computed(() => [
  {
    value: 'overview',
    label: t('workflowDetail.tabs.overview'),
    icon: 'i-heroicons-clipboard-document-list'
  },
  {
    value: 'additional',
    label: t('workflowDetail.tabs.additionalInformation'),
    icon: 'i-lucide-layers'
  },
])

function hashCode(code: string): string {
  return md5(code).toString()
}

function startRetryCountdown(seconds: number): void {
  retryAfterSeconds.value = seconds
  
  if (retryCountdownInterval) {
    clearInterval(retryCountdownInterval)
  }
  
  retryCountdownInterval = setInterval(() => {
    if (retryAfterSeconds.value && retryAfterSeconds.value > 0) {
      retryAfterSeconds.value--
    } else {
      if (retryCountdownInterval) {
        clearInterval(retryCountdownInterval)
        retryCountdownInterval = null
      }
    }
  }, 1000)
}

function startResendCooldown(): void {
  resendCooldown.value = true
  cooldownTimer.value = RESEND_COOLDOWN_SECONDS

  if (resendCooldownInterval) {
    clearInterval(resendCooldownInterval)
  }

  resendCooldownInterval = setInterval(() => {
    if (cooldownTimer.value && cooldownTimer.value > 0) {
      cooldownTimer.value--
    } else {
      if (resendCooldownInterval) {
        clearInterval(resendCooldownInterval)
        resendCooldownInterval = null
      }
      
      resendCooldown.value = false
      cooldownTimer.value = null
    }
  }, 1000)
}

function handleRetry(): void {
  if (errorType.value === 'rate_limited' && retryAfterSeconds.value && retryAfterSeconds.value > 0) {
    return
  }
  
  errorType.value = null
  errorMessage.value = null
  retryAfterSeconds.value = null
  flowState.value = 'initial'
}

async function requestCode(): Promise<void> {
  if (!slug.value) {
    setError('expired', t('workflowPublic.errors.expired'))
    return
  }

  loading.value = true

  try {
    const result = await api.approvals(slug.value, undefined, {
      disableDefaultErrorHandlers: true,
      validateStatus: () => true
    })
    
    if (result.type === 'accepted') {
      flowState.value = 'code_sent'
      showSuccess(t('workflowPublic.codeSent'))
    } else {
      setError('server_error', t('workflowPublic.errors.serverError'))
    }
  } catch (err) {
    handleApiError(err)
  } finally {
    loading.value = false
  }
}

async function verifyCode(): Promise<void> {
  const code = otpCode.value.join('')
  if (code.length !== 6) return

  flowState.value = 'verifying'
  const hashedCode = hashCode(code)

  try {
    const result = await api.approvals(slug.value, hashedCode, {
      disableDefaultErrorHandlers: true,
      validateStatus: () => true
    })

    if (result.type === 'success') {
      workflow.value = deserializeJson(result.data.workflowDto)
      workflowEditStore.setEditableWorkflow(workflow.value)
      slugToken.value = result.data.confirmationToken
      sessionStorage.setItem('slug-token-' + slug.value, result.data.confirmationToken)
      flowState.value = 'verified'
      
      loadDriverData();
      loadContactPersonData();
      loadCostCenterData();
      loadSupplierSalesPersonData();
      loadSupplierData();
      loadContentManagerItems();
      showSuccess(t('workflowPublic.verified'))
    } else {
      setError('server_error', t('workflowPublic.errors.serverError'))
    }
  } catch (err) {
    handleApiError(err, {
      onInvalidCode: () => {
        flowState.value = 'code_sent'
        otpCode.value = []
        showError(t('workflowPublic.errors.invalidCode'))
      },
      onOtherError: () => {
        flowState.value = 'code_sent'
        showError(t('workflowPublic.errors.serverError'))
      }
    })
  }
}

async function resendCode(): Promise<void> {
  if (resendDisabled.value) return

  startResendCooldown()
  resendCount.value++

  try {
    const result = await api.resend(slug.value, {
      disableDefaultErrorHandlers: true,
      validateStatus: () => true
    })

    if (result.type === 'accepted' || result.type === 'success') {
      showSuccess(t('workflowPublic.codeResent'))
    } else {
      showError(t('workflowPublic.errors.serverError'))
    }
  } catch (err) {
    handleApiError(err, {
      onOtherError: () => showError(t('workflowPublic.errors.serverError'))
    })
  }
}

function handleApiError(err: unknown, options?: {
  onInvalidCode?: () => void
  onOtherError?: () => void
}): void {
  if (isApiException(err)) {
    if (err.status === 401 && options?.onInvalidCode) {
      options.onInvalidCode()
    } else if (err.status === 404) {
      setError('expired', t('workflowPublic.errors.expired'))
    } else if (err.status === 429) {
      const retryAfter = parseInt(err.headers?.['retry-after'] || '60')
      setError('rate_limited', t('workflowPublic.errors.rateLimited'), retryAfter)
    } else if (options?.onOtherError) {
      options.onOtherError()
    } else {
      setError('server_error', t('workflowPublic.errors.serverError'))
    }
  } else if (options?.onOtherError) {
    options.onOtherError()
  } else {
    setError('server_error', t('workflowPublic.errors.serverError'))
  }
}

function setError(type: ErrorType, message: string, retryAfter?: number): void {
  flowState.value = 'error'
  errorType.value = type
  errorMessage.value = message
  retryAfterSeconds.value = retryAfter ?? null
  
  if (retryAfter) {
    startRetryCountdown(retryAfter)
  }
}

async function loadDriverData() {
  const driverId = workflow.value?.driverId;
  const externalReference = workflow.value?.externalReference;

  if (!driverId || !externalReference) {
    driver.value = null;
    return;
  }

  driverLoading.value = true;
  driverError.value = false;

  try {
    const token = slugToken.value || sessionStorage.getItem('slug-token-' + slug.value) || '';

    const driverData = await apiAdapter.invoke<DriverDto>(
        requestConfig => api.driver(externalReference, {
          ...requestConfig,
          headers: {
            ...requestConfig?.headers,
            'X-Slug-Token': token
          }
        }),
        () => {}
    );
    
    if (driverData) {
      driver.value = driverData;
    }
  } catch (e) {
    driverError.value = true;
  } finally {
    driverLoading.value = false;
  }
}

async function loadContactPersonData() {
  const contactPersonId = workflow.value?.contactPersonId;
  const externalReference = workflow.value?.externalReference;
  
  if (!contactPersonId || !externalReference) {
    contactPerson.value = null;
    return;
  }
  
  contactPersonLoading.value = true;
  contactPersonError.value = false;
  
  try {
    const token = slugToken.value || sessionStorage.getItem('slug-token-' + slug.value) || '';

    const contactPersonData = await apiAdapter.invoke<ContactPersonDto>(
        requestConfig => api.contactPerson(externalReference, {
          ...requestConfig,
          headers: {
            ...requestConfig?.headers,
            'X-Slug-Token': token
          }
        }),
        () => {}
    );
    
    if (contactPersonData) {
      contactPerson.value = contactPersonData;
    }
  } catch (e) {
    contactPersonError.value = true;
  } finally {
    contactPersonLoading.value = false;
  }
}

async function loadCostCenterData() {
  const costCenterId = workflow.value?.costCenterId;
  const externalReference = workflow.value?.externalReference;
  
  if (!costCenterId || !externalReference) {
    costCenterError.value = false;
    return;
  }
  
  costCenterLoading.value = true;
  costCenterError.value = false;
  
  try {
    const token = slugToken.value || sessionStorage.getItem('slug-token-' + slug.value) || '';
    
    const costCenterData = await apiAdapter.invoke<CostCenterDto>(
        requestConfig => api.costCenter(externalReference, {
          ...requestConfig,
          headers: {
            ...requestConfig?.headers,
            'X-Slug-Token': token
          }
        }),
        () => {}
    );
    
    if (costCenterData) {
      costCenter.value = costCenterData;
    }
  } catch (e) {
    costCenterError.value = true;
  } finally {
    costCenterLoading.value = false;
  }
}

const reloadWorkflow = async (workflowData?: WorkflowDto, _etag?: string) => {
  if (workflowData) {
    workflow.value = deserializeJson(workflowData)
    workflowEditStore.setEditableWorkflow(workflow.value)
    
    loadDriverData();
    loadContactPersonData();
    loadCostCenterData();
    loadSupplierSalesPersonData();
    loadSupplierData();
    loadContentManagerItems();
  }
}

async function loadSupplierSalesPersonData() {
  const supplierSalesPersonId = workflow.value?.supplierSalesPersonId;
  const externalReference = workflow.value?.externalReference;
  
  if (!supplierSalesPersonId || !externalReference) {
    supplierSalesPerson.value = null;
    return;
  }
  
  supplierSalesPersonLoading.value = true;
  supplierSalesPersonError.value = false;
  
  try {
    const token = slugToken.value || sessionStorage.getItem('slug-token-' + slug.value) || '';
    
    const supplierSalesPersonData = await apiAdapter.invoke<SupplierSalesPersonDto>(
        requestConfig => api.supplierSalesperson(externalReference, {
          ...requestConfig,
          headers: {
            ...requestConfig?.headers,
            'X-Slug-Token': token
          }
        }),
        () => {}
    );
    
    if (supplierSalesPersonData) {
      supplierSalesPerson.value = supplierSalesPersonData;
    }
  } catch (e) {
    supplierSalesPersonError.value = true;
  } finally {
    supplierSalesPersonLoading.value = false;
  }
}

async function loadSupplierData() {
  const externalReference = workflow.value?.externalReference;
  const hasDealer = !!workflow.value?.quotation?.dealerId;
  
  if (!externalReference || !hasDealer) {
    supplier.value = null;
    supplierError.value = false;
    return;
  }
  
  supplierLoading.value = true;
  supplierError.value = false;
  
  try {
    const token = slugToken.value || sessionStorage.getItem('slug-token-' + slug.value) || '';
    const supplierData = await apiAdapter.invoke<OrganizationPartnerDto>(
      requestConfig => api.dealer(externalReference, {
        ...requestConfig,
        headers: {
          ...requestConfig?.headers,
          'X-Slug-Token': token
        }
      }),
      () => {}
    );
    
    supplier.value = supplierData ?? null;
  } catch (e) {
    supplierError.value = true;
  } finally {
    supplierLoading.value = false;
  }
}

async function loadContentManagerItems() {
  const externalReference = workflow.value?.externalReference;
  
  if (!externalReference) return;
  
  const token = slugToken.value || sessionStorage.getItem('slug-token-' + slug.value) || '';
  
  try {
    const items = await apiAdapter.invoke<ItemDto[]>(
      requestConfig => api.getWorkflowContentManager(externalReference, {
        ...requestConfig,
        headers: {
          'Accept': 'application/json',
          ...requestConfig?.headers,
          'X-Slug-Token': token
        }
      }),
      () => {}
    );
    
    if (items) {
      contentManagerItems.value = items;
    }
  } catch (e) {
    
  }
}

async function downloadPdf() {
  const externalReference = workflow.value?.externalReference;
  
  if (!externalReference) return;
  
  pdfDownloading.value = true;
  
  let url: string | null = null;
  
  try {
    const item = activeContentManagerItem.value;
    
    if (!item) {
      showError(t('workflowDetail.header.pdfNotFound'));
      return;
    }
    
    const token = slugToken.value || sessionStorage.getItem('slug-token-' + slug.value) || '';
    
    const result = await apiAdapter.invokeRaw(
      (config) => api.downloadWorkflowContentManager(externalReference, { pid: item.pid }, {
        ...config,
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
          ...config?.headers,
          'X-Slug-Token': token
        }
      }) as any,
      t('workflowDetail.header.pdfDownloadError')
    );
    
    if (result?.type === 'success' && result.response) {
      const blob = result.response.data as Blob;
      url = window.URL.createObjectURL(blob);
      
      const contentDisposition = result.response.headers?.['content-disposition'] as string | undefined;
      const fileName = contentDisposition?.match(/filename="?(.+?)"?$/)?.[1] ?? item.fileName ?? `${item.pid}.pdf`;
      const link = document.createElement('a');
      
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } finally {
    if (url) window.URL.revokeObjectURL(url);
    
    pdfDownloading.value = false;
  }
}

onUnmounted(() => {
  if (retryCountdownInterval) {
    clearInterval(retryCountdownInterval)
  }
  
  if (resendCooldownInterval) {
    clearInterval(resendCooldownInterval)
  }
})
</script>